import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall, PhoneIncoming, Siren, ClipboardCheck, CheckCircle2, Timer,
  PlusCircle, AlertTriangle, ArrowRight, Users, AlertOctagon,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import KPICard from "@/components/common/KPICard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import HotlineStatusBadge from "./HotlineStatusBadge";
import SlaBadge from "./SlaBadge";
import { DECISION_LABELS } from "./constants";

const PIE_COLORS = ["#0B7E8C", "#06B6D4", "#6366F1", "#F59E0B", "#10B981", "#EF4444", "#94A3B8"];

export default function HotlineDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const r = await api.get("/hotline/dashboard");
        setData(r.data);
      } finally {
        setLoading(false);
      }
    }
    load();
    // Auto-refresh every 30s to keep SLA timers live
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const k = data?.kpis || {};

  return (
    <div data-testid="hotline-dashboard">
      <PageHeader
        title="Nurse Hotline Command Center"
        subtitle="Live triage queue, escalations, follow-ups, and outcomes — what's happening right now."
        actions={
          <Button onClick={() => navigate("/hotline/new")} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-new-session">
            <PlusCircle className="w-4 h-4 mr-1.5" /> New Hotline Session
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard testId="hotline-kpi-today" icon={PhoneIncoming} label="Calls Today" value={k.total_today ?? "—"} accent="#0B7E8C" />
        <KPICard testId="hotline-kpi-active" icon={PhoneCall} label="Active Sessions" value={k.active_sessions ?? "—"} accent="#6366F1" />
        <KPICard testId="hotline-kpi-escalated" icon={Siren} label="Escalated Cases" value={k.escalated ?? "—"} accent="#EF4444" />
        <KPICard testId="hotline-kpi-followups" icon={ClipboardCheck} label="Follow-Ups" value={k.follow_ups ?? "—"} accent="#F59E0B" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <KPICard testId="hotline-kpi-closed" icon={CheckCircle2} label="Closed Sessions" value={k.closed ?? "—"} accent="#10B981" />
        <KPICard testId="hotline-kpi-aht" icon={Timer} label="Avg Handling Time (min)" value={k.avg_handling_minutes ?? 0} accent="#06B6D4" />
        <KPICard testId="hotline-kpi-sla-breaches" icon={AlertOctagon} label="SLA Breaches" value={k.sla_breaches ?? 0} accent="#EF4444" />
        <KPICard testId="hotline-kpi-sla-warnings" icon={AlertTriangle} label="SLA At-Risk" value={k.sla_warnings ?? 0} accent="#F59E0B" />
      </div>

      {/* At-Risk SLA banner (shown when there are breaches) */}
      {(data?.at_risk_queue || []).length > 0 && (
        <div className="mt-4 hah-card p-5 border-l-4 border-l-[#EF4444]" data-testid="hotline-sla-card">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="hah-section-title text-[#EF4444]">SLA Watch</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-[#EF4444]" />
                Sessions breaching or approaching SLA
              </h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Thresholds: decision pending {(data?.sla_thresholds?.decision_pending || [15, 30])[1]}m · in assessment {(data?.sla_thresholds?.in_assessment || [15, 25])[1]}m · escalated {(data?.sla_thresholds?.escalated || [10, 20])[1]}m · new {(data?.sla_thresholds?.new || [5, 10])[1]}m
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-[#E5E7EB]"
              data-testid="hotline-sla-refresh"
              onClick={async () => {
                await api.post("/hotline/sla/check");
                const r = await api.get("/hotline/dashboard");
                setData(r.data);
              }}
            >
              <Timer className="w-3.5 h-3.5 mr-1.5" /> Refresh now
            </Button>
          </div>
          <div className="space-y-2">
            {data.at_risk_queue.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/hotline/sessions/${s.id}`)}
                data-testid={`hotline-sla-row-${s.id}`}
                className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#EF444412]/30 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-[#0B7E8C] font-medium">{s.record_id}</span>
                    <span className="font-medium text-[#1F2937]">{s.patient?.first_name} {s.patient?.last_name}</span>
                    <span className="text-xs text-[#6B7280]">{s.patient?.mrn}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] truncate mt-0.5">{s.chief_complaint?.description || "—"}</div>
                </div>
                <HotlineStatusBadge status={s.status} />
                <SlaBadge sla={s.sla} testId={`sla-${s.id}`} />
                <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Queues */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <QueueCard
          title="Active Sessions Queue"
          subtitle="What needs attention"
          testId="hotline-active-queue"
          items={data?.active_queue || []}
          empty="No active sessions."
          onItemClick={(s) => navigate(`/hotline/sessions/${s.id}`)}
          renderItem={(s) => (
            <>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1F2937]">{s.patient?.first_name} {s.patient?.last_name}</span>
                  <span className="text-xs text-[#6B7280] font-mono">{s.record_id}</span>
                </div>
                <div className="text-xs text-[#6B7280] truncate">{s.chief_complaint?.description || "—"}</div>
              </div>
              <SlaBadge sla={s.sla} />
              <HotlineStatusBadge status={s.status} />
            </>
          )}
        />
        <QueueCard
          title="Escalation Queue"
          subtitle="Triage / dispatch"
          testId="hotline-escalation-queue"
          items={data?.escalation_queue || []}
          empty="No open escalations."
          renderItem={(e) => (
            <>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1F2937] capitalize">{e.type.replace(/_/g, " ")}</span>
                  <span
                    className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
                    style={{
                      background: e.priority === "high" ? "#EF444414" : "#F59E0B14",
                      color: e.priority === "high" ? "#EF4444" : "#F59E0B",
                    }}
                  >
                    {e.priority}
                  </span>
                </div>
                <div className="text-xs text-[#6B7280] truncate">{e.reason}</div>
              </div>
              <span className="text-xs text-[#6B7280] capitalize">{e.status.replace(/_/g, " ")}</span>
            </>
          )}
        />
        <QueueCard
          title="Follow-Up Queue"
          subtitle="Scheduled tasks"
          testId="hotline-followup-queue"
          items={data?.follow_up_queue || []}
          empty="No pending follow-ups."
          renderItem={(f) => (
            <>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1F2937]">Due {f.due_date}</span>
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#0B7E8C14] text-[#0B7E8C]">{f.priority}</span>
                </div>
                <div className="text-xs text-[#6B7280] truncate">{f.reason}</div>
              </div>
              <span className="text-xs text-[#6B7280] capitalize">{f.status.replace(/_/g, " ")}</span>
            </>
          )}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="hah-card p-5" data-testid="hotline-outcomes-chart">
          <div className="hah-section-title">Outcomes</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Call outcomes distribution</h3>
          <div className="h-56 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.outcomes || []} dataKey="value" nameKey="name" innerRadius={44} outerRadius={78} paddingAngle={2}>
                  {(data?.outcomes || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, DECISION_LABELS[n] || n]} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-1">
            {(data?.outcomes || []).map((o, i) => (
              <div key={o.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-[#1F2937]">{DECISION_LABELS[o.name] || o.name}</span>
                </div>
                <span className="font-medium text-[#1F2937]">{o.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hah-card p-5" data-testid="hotline-workload-chart">
          <div className="hah-section-title">Workload</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Active sessions by nurse</h3>
          <div className="h-56 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.nurse_workload || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} interval={0} angle={-12} textAnchor="end" height={50} />
                <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <Bar dataKey="value" fill="#0B7E8C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm text-[#6B7280]" data-testid="hotline-loading">Loading…</div>
      )}
    </div>
  );
}

function QueueCard({ title, subtitle, items, empty, renderItem, onItemClick, testId }) {
  return (
    <div className="hah-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="hah-section-title">{subtitle}</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">{title}</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#6B7280] py-6 text-center">{empty}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => onItemClick && onItemClick(it)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#EAF8FA]/40 text-left transition-colors"
            >
              {renderItem(it)}
              {onItemClick && <ArrowRight className="w-4 h-4 text-[#94A3B8]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
